import { LOCAL } from "@mn/shared";
import { Parser } from "hot-formula-parser";
import { useMemo, useRef, useState } from "react";
import type * as Y from "yjs";
import { useT } from "@/lib/i18n";
import { useBoard } from "../BoardContext";
import { useDeepVersion } from "../hooks";
import { useBoardUI } from "../store";

const colName = (c: number) => {
  let s = "";
  let n = c;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
};

/** Spreadsheet-like table. Cells live in a Y.Map keyed "r:c"; formulas start with "=" and use A1 references. */
export function TableBody({ id, ymap }: { id: string; ymap: Y.Map<unknown> }) {
  const { handle, canEdit } = useBoard();
  const t = useT();
  const cells = ymap.get("cells") as Y.Map<string>;
  useDeepVersion(cells);
  const rows = (ymap.get("rows") as number) ?? 3;
  const cols = (ymap.get("cols") as number) ?? 3;
  const title = (ymap.get("title") as string) ?? "";
  const widths = (ymap.get("colWidths") as number[] | undefined) ?? [];
  const selected = useBoardUI((s) => s.selection.has(id));
  const [active, setActive] = useState<{ r: number; c: number } | null>(null);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const tx = (fn: () => void) => handle.doc.transact(fn, LOCAL);
  const raw = (r: number, c: number) => cells.get(`${r}:${c}`) ?? "";

  // Evaluate formulas; cell refs resolve recursively through the parser (cycles return #CYCLE).
  const values = useMemo(() => {
    const parser = new Parser();
    const stack = new Set<string>();
    const evalCell = (r: number, c: number): string | number => {
      const v = raw(r, c);
      if (!v.startsWith("=")) return /^-?\d+(\.\d+)?$/.test(v.trim()) ? Number(v) : v;
      const key = `${r}:${c}`;
      if (stack.has(key)) return "#CYCLE";
      stack.add(key);
      const res = parser.parse(v.slice(1));
      stack.delete(key);
      return res.error ? `#${res.error.replace("#", "")}` : (res.result as string | number);
    };
    parser.on(
      "callCellValue",
      (cell: { row: { index: number }; column: { index: number } }, done: (v: unknown) => void) =>
        done(evalCell(cell.row.index, cell.column.index)),
    );
    parser.on(
      "callRangeValue",
      (
        s: { row: { index: number }; column: { index: number } },
        e: { row: { index: number }; column: { index: number } },
        done: (v: unknown) => void,
      ) => {
        const out: unknown[][] = [];
        for (let r = s.row.index; r <= e.row.index; r++) {
          const row: unknown[] = [];
          for (let c = s.column.index; c <= e.column.index; c++) row.push(evalCell(r, c));
          out.push(row);
        }
        done(out);
      },
    );
    const out: (string | number)[][] = [];
    for (let r = 0; r < rows; r++) {
      const row: (string | number)[] = [];
      for (let c = 0; c < cols; c++) row.push(evalCell(r, c));
      out.push(row);
    }
    return out;
    // biome-ignore lint/correctness/useExhaustiveDependencies: cells content is tracked by useDeepVersion
  }, [rows, cols, cells, useDeepVersion(cells)]);

  const start = (r: number, c: number) => {
    if (!canEdit) return;
    setActive({ r, c });
    setDraft(raw(r, c));
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  const commit = (move?: "down" | "right" | "up" | "left") => {
    if (!active) return;
    const { r, c } = active;
    tx(() => (draft === "" ? cells.delete(`${r}:${c}`) : cells.set(`${r}:${c}`, draft)));
    setActive(null);
    if (move === "down" && r + 1 < rows) start(r + 1, c);
    if (move === "right" && c + 1 < cols) start(r, c + 1);
    if (move === "up" && r > 0) start(r - 1, c);
    if (move === "left" && c > 0) start(r, c - 1);
  };
  const addRow = () => tx(() => ymap.set("rows", rows + 1));
  const addCol = () => tx(() => ymap.set("cols", cols + 1));
  const delRow = () => {
    if (rows <= 1) return;
    tx(() => {
      for (let c = 0; c < cols; c++) cells.delete(`${rows - 1}:${c}`);
      ymap.set("rows", rows - 1);
    });
  };
  const delCol = () => {
    if (cols <= 1) return;
    tx(() => {
      for (let r = 0; r < rows; r++) cells.delete(`${r}:${cols - 1}`);
      ymap.set("cols", cols - 1);
    });
  };
  const fmt = (v: string | number) =>
    typeof v === "number"
      ? Number.isInteger(v)
        ? v.toLocaleString()
        : v.toLocaleString(undefined, { maximumFractionDigits: 4 })
      : v;

  return (
    <div className="px-2 py-2" onPointerDown={(e) => selected && e.stopPropagation()}>
      <input
        value={title}
        readOnly={!canEdit}
        placeholder={t.tableTitle}
        onChange={(e) => tx(() => ymap.set("title", e.target.value))}
        className="mb-1 w-full bg-transparent px-1 text-[14px] font-semibold outline-none placeholder:text-dim"
      />
      <div className="overflow-auto" onWheel={(e) => e.stopPropagation()}>
        <table className="border-collapse text-[12px]">
          <thead>
            <tr>
              <th className="w-6 border border-border bg-panel-3 text-[10px] text-dim" />
              {Array.from({ length: cols }, (_, c) => (
                <th
                  key={c}
                  className="border border-border bg-panel-3 px-1 text-[10px] font-normal text-dim"
                  style={{ minWidth: widths[c] ?? 90 }}
                >
                  {colName(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }, (_, r) => (
              <tr key={r}>
                <td className="border border-border bg-panel-3 text-center text-[10px] text-dim">{r + 1}</td>
                {Array.from({ length: cols }, (_, c) => {
                  const isActive = active?.r === r && active.c === c;
                  const v = values[r]?.[c] ?? "";
                  return (
                    <td
                      key={c}
                      className={`border border-border px-1.5 py-1 align-top ${typeof v === "number" ? "text-right tabular-nums" : ""} ${String(v).startsWith("#") ? "text-error" : ""}`}
                      style={{ minWidth: widths[c] ?? 90, height: 26 }}
                      onDoubleClick={() => start(r, c)}
                      onClick={() => active && !isActive && commit()}
                    >
                      {isActive ? (
                        <input
                          ref={inputRef}
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onBlur={() => commit()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.preventDefault(), commit(e.shiftKey ? "up" : "down");
                            else if (e.key === "Tab")
                              e.preventDefault(), commit(e.shiftKey ? "left" : "right");
                            else if (e.key === "Escape") setActive(null);
                          }}
                          className="w-full bg-transparent outline-none"
                        />
                      ) : (
                        <span className="block min-h-[1em] whitespace-pre">{fmt(v)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {canEdit ? (
        <div className="mt-1 flex gap-1 text-[11px] text-dim">
          <button
            type="button"
            onClick={addRow}
            className="rounded-sm px-1.5 hover:bg-panel-2 hover:text-text"
          >
            + {t.row}
          </button>
          <button
            type="button"
            onClick={addCol}
            className="rounded-sm px-1.5 hover:bg-panel-2 hover:text-text"
          >
            + {t.column}
          </button>
          <button
            type="button"
            onClick={delRow}
            className="rounded-sm px-1.5 hover:bg-panel-2 hover:text-text"
          >
            − {t.row}
          </button>
          <button
            type="button"
            onClick={delCol}
            className="rounded-sm px-1.5 hover:bg-panel-2 hover:text-text"
          >
            − {t.column}
          </button>
          <span className="ml-auto">{t.formulaHint}</span>
        </div>
      ) : null}
    </div>
  );
}
